import { useQuery } from '@tanstack/react-query'
import { HandCoins, Landmark } from 'lucide-react'
import type { ReactNode } from 'react'

import { AGEING_BUCKET_LABELS, parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  QueryState,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchFinanceEntry } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'
import { ORIGIN_LABELS } from '../lib/finance-labels.ts'
import {
  FINANCE_STATUS_META,
  overdueMeta,
} from '../lib/finance-status-meta.tsx'

import { DocumentStatusBadge } from './status-badge.tsx'

/**
 * A ficha do título — F154.
 *
 * Como em Compras, **quem tem só `finance.read` não tinha clique nenhum**: a
 * coluna de ações do Financeiro só existe para quem pode escrever ou dar
 * baixa. O dono que quisesse saber de onde veio uma parcela, ou por que um
 * título foi cancelado, não tinha por onde — e essas duas perguntas são o que
 * mais se pergunta sobre um título velho.
 *
 * ## Por que o rodapé não diz "Editar"
 *
 * **Não existe formulário de edição de título.** O `FinanceEntryForm` só cria.
 * Um botão "Editar" aqui abriria coisa nenhuma, e um botão que não faz nada é
 * pior do que a ausência dele. O que se faz com um título em aberto é **dar
 * baixa**, e é isso que o rodapé oferece — para quem tem `finance.settle`.
 *
 * ## As baixas, fora do diálogo de baixar
 *
 * O histórico de baixas existia só dentro do `FinanceSettleDialog`, que é uma
 * tela de ESCRITA: para ver o que já foi pago era preciso entrar no lugar onde
 * se paga. Aqui elas são leitura, e por isso alcançam quem só lê.
 */

const Line = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode => (
  <>
    <dt className="text-(--text-muted)">{label}</dt>
    <dd className="text-(--text-primary)">{children}</dd>
  </>
)

export const FinanceEntryDetailDialog = ({
  entryId,
  onClose,
  onSettle,
}: {
  entryId: string
  onClose: () => void
  /**
   * Abrir a baixa a partir daqui.
   *
   * **Quem decide é a TELA**: `finance.settle` e situação diferente de
   * cancelada. Ausente, o botão não existe — e a ficha continua servindo, que
   * é o ponto dela.
   */
  onSettle?: (() => void) | undefined
}): ReactNode => {
  const query = useQuery({
    queryKey: ['finance', 'entry', entryId],
    queryFn: ({ signal }) => fetchFinanceEntry(entryId, signal),
  })

  const entry = query.data?.entry ?? null
  /*
   * A situação, com a cor e a frase que a explicam (F156).
   *
   * O atraso entra no lugar da situação sem ser uma: é um fato sobre hoje
   * (ADR-0021), e o que interessa a quem cobra é o número de dias.
   */
  const meta =
    entry === null
      ? null
      : entry.status === 'open' && entry.isOverdue
        ? overdueMeta(entry.daysOverdue)
        : FINANCE_STATUS_META[entry.status]

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={entry === null ? 'Título' : entry.description}
        description="Conferir o título. Nada muda nesta tela."
      >
        <QueryState
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          what="o título"
          isEmpty={entry === null}
          empty={
            <EmptyState
              icon={<Landmark aria-hidden="true" className="h-8 w-8" />}
              title="Título não encontrado"
              description="Ele pode ter sido apagado enquanto esta lista estava aberta."
            />
          }
        >
          {entry === null || meta === null ? null : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <DocumentStatusBadge meta={meta} />
                <Badge tone="neutral">
                  {entry.direction === 'receivable' ? 'A receber' : 'A pagar'}
                </Badge>
                <span className="text-(length:--text-sm) text-(--text-secondary)">
                  {entry.branchName}
                </span>
              </div>
              {/* A explicação em texto: na ficha há espaço, e depender de hover
                  deixaria de fora quem usa teclado ou celular (F156). */}
              <p className="-mt-2 text-(length:--text-xs) text-(--text-secondary)">
                {meta.what}
              </p>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
                <Line
                  label={
                    entry.direction === 'receivable' ? 'Cliente' : 'Fornecedor'
                  }
                >
                  {entry.partyName}
                </Line>
                {/* De onde nasceu: a pergunta que a lista nunca respondeu. */}
                <Line label="Origem">{ORIGIN_LABELS[entry.originType]}</Line>
                {/* F349: o título a prazo que nasceu de um acerto diz de qual. */}
                {entry.raisedFrom === null ? null : (
                  <Line label="Veio de">
                    {entry.raisedFrom.description} (venc.{' '}
                    {formatDate(entry.raisedFrom.dueAt)}), no acerto
                  </Line>
                )}
                {entry.documentNumber === null ? null : (
                  <Line label="Documento">{entry.documentNumber}</Line>
                )}
                {entry.instalmentCount > 1 ? (
                  <Line label="Parcela">
                    {entry.instalmentNumber}/{entry.instalmentCount}
                  </Line>
                ) : null}
                <Line label="Emissão">{formatDate(entry.issuedAt)}</Line>
                <Line label="Vencimento">{formatDate(entry.dueAt)}</Line>
                {entry.status === 'open' ? (
                  <Line label="Faixa de atraso">
                    {AGEING_BUCKET_LABELS[entry.ageingBucket]}
                  </Line>
                ) : null}
                {entry.settledAt === null ? null : (
                  <Line label="Quitado em">{formatDate(entry.settledAt)}</Line>
                )}
                {entry.cancelledAt === null ? null : (
                  <Line label="Cancelado em">
                    {formatDate(entry.cancelledAt)}
                  </Line>
                )}
                {entry.cancellationReason === null ? null : (
                  <Line label="Motivo do cancelamento">
                    {entry.cancellationReason}
                  </Line>
                )}
                {entry.notes === null ? null : (
                  <Line label="Observação">{entry.notes}</Line>
                )}
                <Line label="Valor">
                  {formatMoney(parseAmount(entry.amount))}
                </Line>
                <Line label="Já pago">
                  {formatMoney(parseAmount(entry.settledAmount))}
                </Line>
                <dt className="font-(--weight-semibold) text-(--text-primary)">
                  Em aberto
                </dt>
                <dd className="font-(--weight-semibold) text-(--text-primary)">
                  {formatMoney(parseAmount(entry.outstanding))}
                </dd>
              </dl>

              {entry.settlements.length === 0 ? null : (
                <div className="flex flex-col gap-2">
                  <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                    Baixas
                  </h3>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>Quando</TableHeaderCell>
                          <TableHeaderCell numeric>Pago</TableHeaderCell>
                          <TableHeaderCell>Observação</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entry.settlements.map((settlement) => (
                          <TableRow key={settlement.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDate(settlement.settledOn)}
                              {/* Uma baixa estornada continua na lista, e
                                  precisa se anunciar: some-la reescreveria o
                                  histórico do título. */}
                              {settlement.isReversed ? (
                                <Badge tone="neutral" className="ml-2">
                                  Estornada
                                </Badge>
                              ) : null}
                              {/* O estorno também é uma linha: sem o nome,
                                  parecia um segundo pagamento (pedido 1516). */}
                              {settlement.reversesSettlementId ===
                              null ? null : (
                                <Badge tone="neutral" className="ml-2">
                                  Estorno
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell numeric>
                              {formatMoney(parseAmount(settlement.paidAmount))}
                            </TableCell>
                            <TableCell className="text-(--text-secondary)">
                              {settlement.note ?? '—'}
                              {settlement.raisedEntry === null ? null : (
                                <span className="block text-(length:--text-xs)">
                                  Abriu o título a prazo de{' '}
                                  {formatDate(settlement.raisedEntry.dueAt)}
                                  {settlement.raisedEntry.status === 'cancelled'
                                    ? ' (cancelado)'
                                    : ''}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              )}
            </div>
          )}
        </QueryState>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {onSettle === undefined ? null : (
            <Button
              variant="primary"
              leadingIcon={<HandCoins aria-hidden="true" className="h-4 w-4" />}
              onClick={onSettle}
            >
              {entry?.status === 'open' ? 'Baixar' : 'Ver baixas'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
